import React, { type Key, useState } from 'react';

import { RedoOutlined } from '@ant-design/icons';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryDefinition,
} from '@reduxjs/toolkit/dist/query';
import { type UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { Flex, Input, Space, Table, type TableProps, Tooltip, Typography } from 'antd';
import { type SorterResult, type TableRowSelection } from 'antd/lib/table/interface';
import { type Pagination } from 'models/pagination';
import { type Role, type User } from 'models/user';
import { useAppDispatch, useAppSelector } from 'services/redux/configureStore';
import { setElemsPerPage } from 'services/redux/pages/slice';
import { type FetchUsers } from 'services/rtk/users/users';

import { userTableModel } from '../index';
import type { DataType } from '../model';
import styles from './view.module.css';

const { Search } = Input;
const { Text } = Typography;

export type ExtraColumn = 'teamLead' | 'teamRole' | 'edit' | 'isActive' | 'createdAt';

export interface UserTableDefaultProps {
  query:
    | UseQuery<
        QueryDefinition<
          FetchUsers,
          BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>,
          any,
          { data: User[]; pagination: Pagination },
          'api'
        >
      >
    | UseQuery<
        QueryDefinition<
          FetchUsers & { id: number },
          BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>,
          any,
          { data: User[]; pagination: Pagination },
          'api'
        >
      >;
  panelButton?: React.ReactNode;
  rowSelection?: TableRowSelection<DataType>;
  selectedRowKeys?: Key[];
  userRoleFilterValues: Role[];
  defaultRoles?: Role[];
  otherParams?: Record<string, any>;
  extraColumns?: ExtraColumn[];
  onEditClick?: VoidFunction;
  setUser?: (user: User) => void;
}

export default function UsersTableDefault({
  extraColumns,
  defaultRoles,
  rowSelection,
  selectedRowKeys,
  userRoleFilterValues,
  panelButton,
  query,
  otherParams,
  onEditClick,
  setUser,
}: UserTableDefaultProps) {
  const dispatch = useAppDispatch();

  const { elemsPerPage } = useAppSelector((state) => state.pages['users']);

  const [page, setPage] = useState(1);

  const { filters, actions: filterActions } = userTableModel.useFilters({ defaultRoles });

  const columns = userTableModel.useColumns({
    filters,
    userRoleFilterValues,
    extraColumns,
    onEditClick,
    setUser,
  });

  const {
    data: users,
    pagination: usersPagination,
    isFetching,
  } = userTableModel.useQueryFetchData({
    query,
    filters,
    page,
    perPage: elemsPerPage,
    otherParams,
    userRoleFilterValues,
    showIsActiveColumn: extraColumns?.includes('isActive'),
  });

  const handleChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter) => {
    const { current, pageSize } = pagination;

    filterActions.setSort(sorter as SorterResult<DataType>);
    filterActions.setFilterInfo(filters);

    if (pageSize !== elemsPerPage && pageSize) {
      setPage(1);
      dispatch(setElemsPerPage({ page: 'users', elemsPerPage: pageSize }));
    } else if (current !== page && current) {
      setPage(current);
    }
  };

  return (
    <>
      <Flex className={styles.header}>
        <Space>
          <Text>
            {selectedRowKeys && selectedRowKeys?.length > 0 ? `Выбрано: ${selectedRowKeys?.length}` : ''}
          </Text>
          {panelButton}
        </Space>
        <Search
          style={{ maxWidth: '300px' }}
          placeholder={'Поиск'}
          enterButton={'Искать'}
          value={filters.searchString}
          className={styles.header__search}
          onChange={(e) => filterActions.setSearchString(e.target.value)}
          onSearch={filterActions.setSearchFilter}
          loading={isFetching}
          suffix={
            <Tooltip title={'Сбросить фильтры'} trigger="hover" mouseEnterDelay={0.4}>
              <RedoOutlined onClick={filterActions.clearFilters} style={{ cursor: 'pointer' }} />
            </Tooltip>
          }
        />
      </Flex>
      <Table
        id="users-table"
        bordered
        scroll={{ x: 1200 }}
        rowSelection={rowSelection}
        dataSource={users}
        columns={columns}
        sticky
        pagination={{
          pageSize: elemsPerPage,
          total: usersPagination?.totalCount,
          current: page,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
        }}
        onChange={handleChange}
        sortDirections={['ascend', 'descend']}
        loading={isFetching}
        showSorterTooltip={false}
      />
    </>
  );
}
